import { useEffect, useRef, useState, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import Meta from '../components/Meta';
import OnePageStepper from '../components/onepage/OnePageStepper';
import CatalogSection from '../components/onepage/CatalogSection';
import CartSection from '../components/onepage/CartSection';
import ShippingSection from '../components/onepage/ShippingSection';
import PaymentSection from '../components/onepage/PaymentSection';
import SummarySection from '../components/onepage/SummarySection';
import '../components/onepage/onepage.css';

// One-page store: the whole shop-to-checkout flow (browse -> cart -> shipping
// -> payment -> review) on a single scrolling page. Every section reads/writes
// the same Redux slices the wizard used, so state persists identically. The
// sticky stepper is a scroll-spy driven by an IntersectionObserver.
const SECTION_IDS = ['catalog', 'cart', 'shipping', 'payment', 'review'];

const OnePageStoreScreen = () => {
  const [active, setActive] = useState('catalog');
  // true after 送出訂單 was blocked by missing shipping info -> show field errors
  const [showShippingErrors, setShowShippingErrors] = useState(false);
  const refs = useRef({});

  const setRef = (id) => (el) => {
    if (el) refs.current[id] = el;
  };

  const scrollTo = useCallback((id) => {
    const el = refs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.dataset.section);
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );

    SECTION_IDS.forEach((id) => {
      const el = refs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Meta title="線上商店｜小馬麻手作烘焙坊" />
      {/* Phones: the sidebar is hidden, so show the steps as a sticky pill bar */}
      <div className="d-md-none op-mobile-steps">
        <OnePageStepper active={active} onJump={scrollTo} variant="horizontal" />
      </div>
      <Row>
        <Col md={3} lg={2} className="d-none d-md-block">
          <OnePageStepper active={active} onJump={scrollTo} />
        </Col>
        <Col md={9} lg={10}>
          {/* Each step is a light panel (replaces the old <hr /> dividers) */}
          <section ref={setRef('catalog')} data-section="catalog" className="op-panel">
            <CatalogSection />
          </section>
          <section ref={setRef('cart')} data-section="cart" className="op-panel">
            <CartSection scrollTo={scrollTo} />
          </section>
          <section ref={setRef('shipping')} data-section="shipping" className="op-panel">
            <ShippingSection showErrors={showShippingErrors} />
          </section>
          <section ref={setRef('payment')} data-section="payment" className="op-panel">
            <PaymentSection />
          </section>
          <section ref={setRef('review')} data-section="review" className="op-panel">
            <SummarySection
              scrollTo={scrollTo}
              onShippingInvalid={() => setShowShippingErrors(true)}
            />
          </section>
        </Col>
      </Row>
    </>
  );
};

export default OnePageStoreScreen;
